import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import {
  ArrowLeft, Building2, CalendarDays, ChevronRight, Search, TrendingUp, UserCircle,
} from 'lucide-react';
import { apiFetch } from '@/lib/supabaseClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  FilterPanel, Metric, StateBadge, STATI, YearNotice, fmt,
} from '@/pages/FerieAnalisi';
import { getCompanyBrand } from '@/config/brandConfig';

const MONTH_NAMES = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
const EMPTY_FILTERS = { anni: [], aziende: [], locali: [], reparti: [], dipendenti: [], stati: STATI.map(([value]) => value) };

const VIEW_CONFIG = {
  locali: {
    eyebrow: 'Approfondimento · locali',
    title: 'Situazione per locale',
    description: 'Confronta saldi e reparti dei locali autorizzati.',
    icon: Building2,
  },
  dipendenti: {
    eyebrow: 'Approfondimento · persone',
    title: 'Situazione per dipendente',
    description: 'Cerca una persona e apri il dettaglio ferie annuale.',
    icon: UserCircle,
  },
  mensile: {
    eyebrow: 'Approfondimento · andamento',
    title: 'Andamento mensile',
    description: 'Segui maturato, goduto, programmato e disponibilità mese per mese.',
    icon: TrendingUp,
  },
  timeline: {
    eyebrow: 'Approfondimento · periodi',
    title: 'Timeline annuale',
    description: 'Consulta tutti i periodi dell’anno con date, persone, organizzazione e stato.',
    icon: CalendarDays,
  },
};

function valuesFromSearch(search, locationId) {
  const params = new URLSearchParams(search);
  const requestedYear = Number(params.get('anno'));
  const currentYear = new Date().getFullYear();
  return {
    year: requestedYear >= 2000 && requestedYear <= 2200 ? requestedYear : currentYear,
    company: params.get('azienda_id') || 'all',
    location: locationId || params.get('locale_id') || 'all',
    department: params.get('reparto_id') || 'all',
    employee: params.get('dipendente_id') || 'all',
    state: params.get('stato') || 'all',
  };
}

function analyticsParams(values) {
  const params = new URLSearchParams({ anno: String(values.year) });
  if (values.company !== 'all') params.set('azienda_id', values.company);
  if (values.location !== 'all') params.set('locale_id', values.location);
  if (values.department !== 'all') params.set('reparto_id', values.department);
  if (values.employee !== 'all') params.set('dipendente_id', values.employee);
  if (values.state !== 'all') params.set('stato', values.state);
  return params;
}

function useInsightData(locationId) {
  const location = useLocation();
  const [values, setValues] = useState(() => valuesFromSearch(location.search, locationId));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const nextValues = valuesFromSearch(location.search, locationId);
    setValues((current) => Object.keys(nextValues).every((key) => current[key] === nextValues[key]) ? current : nextValues);
  }, [location.search, locationId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await apiFetch(`/api/ferie/analisi?${analyticsParams(values)}`));
    } catch (requestError) {
      setError(requestError.message || 'Analisi non disponibile');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [values]);

  useEffect(() => { load(); }, [load]);
  return { values, setValues, data, loading, error, load };
}

function PageHeader({ config, year, locationId }) {
  const Icon = config.icon;
  const backPath = locationId ? `/ferie/situazione-locale?anno=${year}` : `/ferie?anno=${year}`;
  return (
    <>
      <Link to={backPath} className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-900" data-testid={locationId ? 'ferie-locale-back' : 'ferie-page-back'}>
        <ArrowLeft className="h-4 w-4" /> {locationId ? 'Situazione per locale' : 'Torna a Ferie'}
      </Link>
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex items-start gap-3">
          <span className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-emerald-50 text-brand-green"><Icon className="h-5 w-5" /></span>
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-green">{config.eyebrow} · {year}</div>
            <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight text-brand-navy md:text-4xl">{config.title}</h1>
            <p className="mt-2 max-w-2xl text-sm text-stone-500">{config.description}</p>
          </div>
        </div>
        <Link to={`/ferie?anno=${year}`} className="inline-flex items-center self-start rounded-full border border-app-border bg-app-surface px-4 py-2 text-sm font-semibold text-stone-700 hover:border-brand-green sm:self-auto">
          Gestione ferie
        </Link>
      </header>
    </>
  );
}

function KpiGrid({ kpi, year }) {
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
      <Metric label="Maturato" value={kpi.maturato} />
      <Metric label="Riportato" value={kpi.riportato} />
      <Metric label="Goduto" value={kpi.goduto} />
      <Metric label="Programmato" value={kpi.programmato} tone="warn" />
      <Metric label="Rettifiche" value={kpi.rettifiche} tone="info" />
      <Metric label="Disponibile" displayLabel="GIORNI DISPONIBILI" value={kpi.disponibile} tone="good" highlight hint={`Saldo disponibile al 31/12/${year}`} />
    </div>
  );
}

function SmallKpis({ row }) {
  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3">
      <SmallMetric label="Maturato" value={row.maturato} />
      <SmallMetric label="Riportato" value={row.riportato} />
      <SmallMetric label="Goduto" value={row.goduto} />
      <SmallMetric label="Programmato" value={row.programmato} />
      <SmallMetric label="Rettifiche" value={row.rettifiche} />
      <SmallMetric label="Disponibile" value={row.disponibile} good />
    </div>
  );
}

function SmallMetric({ label, value, good = false }) {
  return <div><div className={`font-display text-sm font-semibold ${good ? 'text-brand-green' : 'text-stone-800'}`}>{fmt(value)}g</div><div className="text-[9px] font-bold uppercase tracking-widest text-stone-400">{label}</div></div>;
}

function LocalesView({ rows, year }) {
  return (
    <section className="space-y-3" data-testid="ferie-analisi-locations">
      <div className="flex items-end justify-between gap-3"><div><h2 className="font-display text-lg font-semibold text-brand-navy">Locali autorizzati</h2><p className="text-sm text-stone-500">{rows.length} locali nel perimetro selezionato.</p></div></div>
      {rows.length === 0 ? <EmptyState message="Nessun locale disponibile per questi filtri." /> : (
        <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
          {rows.map((row) => (
            (() => {
              const brand = getCompanyBrand(row.aziendaNome, row.aziendaId);
              return (
                <Link
                  key={row.id || row.nome}
                  to={row.id ? `/ferie/situazione-locale/${row.id}?anno=${year}` : '/ferie/situazione-locale'}
                  data-testid={`analisi-locale-${row.id || 'senza-locale'}`}
                  data-brand-key={brand.key}
                  style={{ '--locale-brand': brand.accent, '--locale-brand-soft': brand.soft, '--locale-brand-border': brand.border }}
                  className="group rounded-2xl border border-app-border bg-app-surface p-4 transition-colors hover:border-[var(--locale-brand-border)] hover:bg-[var(--locale-brand-soft)] active:border-[var(--locale-brand-border)] active:bg-[var(--locale-brand-soft)] focus-visible:border-[var(--locale-brand-border)] focus-visible:bg-[var(--locale-brand-soft)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--locale-brand)]/20"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex min-w-0 items-center gap-2">
                        <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: brand.accent }} data-testid="ferie-locale-brand-accent" aria-hidden="true" />
                        <h3 className="truncate font-display font-semibold text-brand-navy">{row.nome}</h3>
                      </div>
                      <p className="mt-1 truncate text-xs text-stone-500">{row.aziendaNome || 'Azienda'} · {row.dipendenti} dipendenti</p>
                    </div>
                <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-stone-300" />
                  </div>
                  <div className="mt-4"><SmallKpis row={row} /></div>
                </Link>
              );
            })()
          ))}
        </div>
      )}
    </section>
  );
}

function DepartmentTable({ rows }) {
  return (
    <section className="overflow-hidden rounded-2xl border border-app-border bg-app-surface" data-testid="ferie-analisi-departments">
      <div className="border-b border-stone-100 px-5 py-4"><h2 className="font-display font-semibold text-brand-navy">Riepilogo per reparto</h2><p className="text-xs text-stone-500">Aggregati calcolati dal server sul locale selezionato.</p></div>
      <div className="divide-y divide-stone-100">
        {rows.length === 0 ? <EmptyState message="Nessun reparto disponibile." /> : rows.map((row) => (
          <div key={row.id || `${row.localeId}-${row.nome}`} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0"><div className="font-display font-semibold text-brand-navy">{row.nome}</div><div className="mt-1 text-xs text-stone-500">{row.dipendenti} dipendenti · {row.aziendaNome || 'Azienda'} · {row.localeNome || 'Locale'}</div></div>
            <div className="sm:min-w-[440px]"><SmallKpis row={row} /></div>
          </div>
        ))}
      </div>
    </section>
  );
}

function EmployeesView({ rows, year }) {
  const [search, setSearch] = useState('');
  const filteredRows = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase('it-IT');
    if (!needle) return rows;
    return rows.filter((row) => [row.displayName, row.aziendaNome, row.localeNome, row.reparto].filter(Boolean).join(' ').toLocaleLowerCase('it-IT').includes(needle));
  }, [rows, search]);
  return (
    <section className="space-y-3" data-testid="ferie-analisi-employees">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div><h2 className="font-display text-lg font-semibold text-brand-navy">Dipendenti nel perimetro</h2><p className="text-sm text-stone-500">{filteredRows.length} di {rows.length} persone visualizzate.</p></div>
        <label className="relative block w-full sm:max-w-xs"><span className="sr-only">Cerca dipendente</span><Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" /><Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Cerca persona, locale o reparto" className="pl-9" data-testid="ferie-analisi-employee-search" /></label>
      </div>
      {filteredRows.length === 0 ? <EmptyState message={rows.length ? 'Nessun risultato per questa ricerca.' : 'Nessun dipendente con questi filtri.'} /> : (
        <div className="divide-y divide-stone-100 overflow-hidden rounded-2xl border border-app-border bg-app-surface">
          {filteredRows.map((row) => (
            <Link key={row.id} to={`/ferie/analisi/${row.id}?anno=${year}`} className="group flex flex-col gap-3 p-4 transition-colors hover:bg-stone-50 sm:flex-row sm:items-center sm:justify-between" data-testid={`analisi-dipendente-${row.id}`}>
              <div className="min-w-0"><div className="truncate font-display font-semibold text-brand-navy">{row.displayName}</div><div className="mt-1 truncate text-xs text-stone-500">{row.aziendaNome || 'Azienda'} · {row.localeNome || 'Locale'} · {row.reparto || 'Reparto n/d'}</div></div>
              <div className="grid grid-cols-3 gap-x-5 gap-y-2 sm:min-w-[440px] sm:grid-cols-6"><SmallMetric label="Maturato" value={row.maturato} /><SmallMetric label="Riportato" value={row.riportato} /><SmallMetric label="Goduto" value={row.goduto} /><SmallMetric label="Prog." value={row.programmato} /><SmallMetric label="Rett." value={row.rettifiche} /><SmallMetric label="Disp." value={row.disponibile} good /></div>
              <ChevronRight className="hidden h-4 w-4 text-stone-300 sm:block" />
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

function MonthlyView({ rows }) {
  return (
    <section className="space-y-3" data-testid="ferie-analisi-monthly">
      <div><h2 className="font-display text-lg font-semibold text-brand-navy">Da gennaio a dicembre</h2><p className="text-sm text-stone-500">Valori aggregati nel perimetro autorizzato.</p></div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
        {(rows || []).map((row) => (
          <article key={row.mese} className="rounded-2xl border border-app-border bg-app-surface p-4">
            <div className="font-display text-sm font-semibold text-brand-navy">{MONTH_NAMES[row.mese - 1] || row.label}</div>
            <div className="mt-4 space-y-2">
              <LineMetric label="Maturato" value={row.maturato} />
              <LineMetric label="Goduto" value={row.goduto} />
              <LineMetric label="Programmato" value={row.programmato} />
              <LineMetric label="Disponibile" value={row.disponibile} good />
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function LineMetric({ label, value, good = false }) {
  return <div className="flex items-center justify-between gap-2 text-xs"><span className="text-stone-500">{label}</span><span className={`font-semibold ${good ? 'text-brand-green' : 'text-stone-800'}`}>{fmt(value)}g</span></div>;
}

function TimelineView({ periods, employees }) {
  const employeesById = useMemo(() => Object.fromEntries(employees.map((row) => [row.id, row])), [employees]);
  const uniquePeriods = useMemo(() => Array.from(new Map((periods || []).map((period) => [period.id, period])).values()), [periods]);
  return (
    <section className="overflow-hidden rounded-2xl border border-app-border bg-app-surface" data-testid="ferie-analisi-timeline">
      <div className="border-b border-stone-100 px-5 py-4"><h2 className="font-display text-lg font-semibold text-brand-navy">Periodi dell’anno</h2><p className="text-sm text-stone-500">Rifiutate e annullate sono visibili ma non incidono sul saldo.</p></div>
      {uniquePeriods.length === 0 ? <EmptyState message="Nessun periodo registrato per questa selezione." /> : (
        <div className="divide-y divide-stone-100">
          {uniquePeriods.map((period) => {
            const employee = employeesById[period.dipendenteId];
            return (
              <div key={period.id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-start sm:justify-between" data-testid={`analisi-periodo-${period.id}`}>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2 text-sm font-semibold text-brand-navy"><span>{period.dal} → {period.al}</span><span className="text-xs font-normal text-stone-500">· {fmt(period.giorni)}g</span></div>
                  <div className="mt-1 text-sm text-stone-800">{employee?.displayName || 'Dipendente non disponibile'}</div>
                  <div className="mt-1 text-xs text-stone-500">{employee?.aziendaNome || 'Azienda'} · {employee?.localeNome || 'Locale'} · {employee?.reparto || 'Reparto n/d'}</div>
                  {period.note && <div className="mt-2 text-xs italic text-stone-500">&ldquo;{period.note}&rdquo;</div>}
                </div>
                <StateBadge value={period.stato} />
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function EmptyState({ message }) {
  return <div className="rounded-2xl border border-dashed border-stone-200 bg-stone-50/60 p-8 text-center text-sm text-stone-500">{message}</div>;
}

export default function FerieAnalisiApprofondimento({ view }) {
  const { localeId } = useParams();
  const config = VIEW_CONFIG[view] || VIEW_CONFIG.locali;
  const { values, setValues, data, loading, error, load } = useInsightData(localeId);
  const filters = data?.filters || EMPTY_FILTERS;
  const rows = data?.perDipendente || [];

  return (
    <div className="space-y-6 pb-20 md:pb-0" data-testid={localeId ? 'ferie-analisi-locale-detail' : `ferie-analisi-${view}-page`}>
      <PageHeader config={config} year={data?.anno || values.year} locationId={localeId} />
      <FilterPanel filters={filters} values={values} setValues={setValues} loading={loading} />
      {loading && <div className="text-sm text-stone-500">Caricamento analisi…</div>}
      {error && <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-center text-sm text-red-800">{error}<div><Button type="button" variant="outline" onClick={load} className="mt-3 rounded-full border-red-200 text-red-800">Riprova</Button></div></div>}
      {!loading && !error && data && (
        <>
          <YearNotice meta={data.metaAnno} />
          <KpiGrid kpi={data.kpi || {}} year={data.anno} />
          {view === 'locali' && (localeId ? (
            <div className="space-y-6">
              <section className="rounded-2xl border border-app-border bg-app-surface p-5">
                <h2 className="font-display text-lg font-semibold text-brand-navy">{data.perLocale?.[0]?.nome || 'Locale selezionato'}</h2>
                <p className="mt-1 text-sm text-stone-500">{data.perLocale?.[0]?.aziendaNome || 'Azienda'} · {data.perLocale?.[0]?.dipendenti || 0} dipendenti</p>
              </section>
              <DepartmentTable rows={data.perReparto || []} />
              <EmployeesView rows={rows} year={data.anno} />
            </div>
          ) : <LocalesView rows={data.perLocale || []} year={data.anno} />)}
          {view === 'dipendenti' && <EmployeesView rows={rows} year={data.anno} />}
          {view === 'mensile' && <MonthlyView rows={data.mensile || []} />}
          {view === 'timeline' && <TimelineView periods={data.periodi || []} employees={rows} />}
        </>
      )}
      <Link to={localeId ? `/ferie/situazione-locale?anno=${values.year}` : `/ferie?anno=${values.year}`} className="inline-flex items-center gap-1 text-sm text-stone-400 hover:text-stone-700" data-testid="ferie-analisi-bottom-back"><ArrowLeft className="h-3.5 w-3.5" /> {localeId ? 'Torna ai locali' : 'Torna a Ferie'}</Link>
    </div>
  );
}